// VendTrade listing detail. Public — anyone can view. Logged-in users
// who don't own the listing get a Contact button that reveals the
// owner's email + fires an email notification to them.
import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, MapPin, ArrowLeft, CheckCircle2 } from "lucide-react";
import { CategoryChip, DirectionPill, DIR_MAP } from "./shared";
import VendAgainCrossPromote from "@/components/VendAgainCrossPromote";

export default function VendTradeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/vendtrade/listings/${id}`);
      setListing(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const contact = async () => {
    if (!user) { nav(`/login?next=/vendtrade/listings/${id}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/vendtrade/listings/${id}/contact`);
      setRevealed({ name: data.owner_name, email: data.owner_email });
      toast.success(`We let ${data.owner_name} know you're interested.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-16 text-center font-serif text-2xl">Loading…</div>;
  if (error) return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-center">
      <p style={{ color: "var(--terracotta)" }}>{error}</p>
      <Link to="/vendtrade" className="underline mt-4 inline-block">Back to VendTrade</Link>
    </div>
  );
  if (!listing) return null;

  const dir = DIR_MAP[listing.direction];

  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="vendtrade-detail-page">
      <Link
        to="/vendtrade"
        className="text-sm inline-flex items-center gap-1"
        style={{ color: "var(--ink-soft)" }}
        data-testid="vendtrade-detail-back"
      >
        <ArrowLeft size={14} /> Back to VendTrade
      </Link>

      <div
        className="mt-6 rounded-lg border overflow-hidden"
        style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
      >
        {listing.image_url && (
          <img
            src={listing.image_url}
            alt={listing.title}
            data-testid="vendtrade-detail-image"
            className="w-full max-h-[420px] object-cover"
          />
        )}
        <div className="p-8">
          <div className="flex items-center justify-between gap-3">
            <DirectionPill value={listing.direction} />
            <CategoryChip value={listing.category} />
          </div>

        <h1
          className="font-serif text-4xl mt-4 leading-tight"
          data-testid="vendtrade-detail-title"
        >
          {listing.title}
        </h1>

        <div
          className="mt-3 text-xs inline-flex items-center gap-1.5"
          style={{ color: "var(--ink-soft)" }}
        >
          <MapPin size={12} /> {listing.city}
          <span className="mx-2">·</span>
          {new Date(listing.created_at).toLocaleDateString()}
        </div>

        <p
          className="mt-6 text-base leading-relaxed whitespace-pre-line"
          data-testid="vendtrade-detail-description"
        >
          {listing.description}
        </p>

        <div className="mt-8 pt-6 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--ink-soft)" }}>
            {dir?.label} posted by
          </div>
          <div className="flex flex-wrap items-baseline gap-2">
            {listing.owner?.slug ? (
              <Link
                to={`/@${listing.owner.slug}`}
                className="font-serif text-xl underline"
              >
                {listing.owner.business_name || listing.owner.name}
              </Link>
            ) : (
              <span className="font-serif text-xl">
                {listing.owner?.business_name || listing.owner?.name || "A community member"}
              </span>
            )}
            {listing.owner?.role && (
              <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(0,0,0,0.05)", color: "var(--ink-soft)" }}>
                {listing.owner.role}
              </span>
            )}
          </div>
        </div>

        <div className="mt-8">
          {listing.is_owner ? (
            <div
              data-testid="vendtrade-detail-owner-note"
              className="rounded-md p-4 text-sm"
              style={{ background: "rgba(111,125,85,0.08)", color: "var(--moss)" }}
            >
              This is your listing. Manage it from
              &nbsp;<Link to="/vendtrade/mine" className="underline">My listings</Link>.
            </div>
          ) : revealed ? (
            <div
              data-testid="vendtrade-detail-revealed"
              className="rounded-md p-5"
              style={{ background: "rgba(111,125,85,0.08)", border: "1px solid rgba(111,125,85,0.2)" }}
            >
              <div className="flex items-center gap-2 font-medium" style={{ color: "var(--moss)" }}>
                <CheckCircle2 size={16} /> Contact info unlocked
              </div>
              <div className="mt-3 text-sm">
                Email <strong>{revealed.name}</strong> at{" "}
                <a
                  href={`mailto:${revealed.email}?subject=${encodeURIComponent("VendTrade: " + listing.title)}`}
                  className="underline"
                  data-testid="vendtrade-detail-mailto"
                >
                  {revealed.email}
                </a>{" "}
                to work out the trade. We already sent them a heads-up.
              </div>
            </div>
          ) : (
            <Button
              onClick={contact}
              disabled={busy}
              data-testid="vendtrade-detail-contact"
              className="btn-primary py-6 px-8 rounded-full inline-flex items-center gap-2"
            >
              <Mail size={16} />
              {busy ? "Reaching out…" : `Contact to trade`}
            </Button>
          )}
          {!user && !listing.is_owner && (
            <p className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>
              You&rsquo;ll need to sign in to see contact info.
            </p>
          )}
        </div>
        </div>
      </div>
      <VendAgainCrossPromote
        city={listing?.city || ""}
        excludeUserId={listing?.owner?.id || null}
      />
    </div>
  );
}
